import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Snackbar,
  Divider,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  List as ListIcon,
  CloudOff as CloudOffIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import offlinePaymentService from '../../services/offlinePaymentService';
import indexedDB from '../../services/indexedDB';
import PostMaturityInterestPanel from '../Common/PostMaturityInterestPanel';

// Event to notify status indicator to refresh
const triggerStatusRefresh = () => {
  window.dispatchEvent(new CustomEvent('offlinePaymentUpdated'));
};

const LoanSchedules = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loans, setLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [filteredInstallments, setFilteredInstallments] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [status, setStatus] = useState('PAID');
  const [notes, setNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [nicFilter, setNicFilter] = useState('');
  const [loanNumberFilter, setLoanNumberFilter] = useState('');
  const [syncMessage, setSyncMessage] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [usingCachedData, setUsingCachedData] = useState(false);
  const [offlinePaymentMap, setOfflinePaymentMap] = useState({}); // Map of installmentId -> hasPendingPayment
  const filterDate = searchParams.get('date'); // Date from query parameter
  const [interestConfig, setInterestConfig] = useState(null); // Interest configuration
  const [totalPayableBreakdown, setTotalPayableBreakdown] = useState(null); // Total payable breakdown for payment dialog
  const [postMaturityBreakdown, setPostMaturityBreakdown] = useState(null);
  const [paymentEditRequests, setPaymentEditRequests] = useState({}); // Map of installmentId -> request
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requestedAmount, setRequestedAmount] = useState('');
  
  // Use refs to avoid infinite loops in event handlers
  const installmentsRef = useRef([]);
  const loanIdRef = useRef(null);
  const selectedLoanRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => {
    installmentsRef.current = installments;
  }, [installments]);
  
  useEffect(() => {
    loanIdRef.current = loanId;
  }, [loanId]);
  
  useEffect(() => {
    selectedLoanRef.current = selectedLoan;
  }, [selectedLoan]);
  
  useEffect(() => {
    fetchInterestConfiguration();
  }, []);
  
  const fetchInterestConfiguration = async () => {
    try {
      const response = await api.get('/collector/interest-configuration');
      setInterestConfig(response.data);
    } catch (error) {
      console.error('Error fetching interest configuration:', error);
    }
  };

  // Function to check which installments have pending offline payments
  const checkOfflinePayments = useCallback(async () => {
    try {
      if (installments.length === 0) return;
      
      const installmentIds = installments.map(inst => inst.id);
      const pendingMap = await indexedDB.getPendingPaymentIdsByInstallmentIds(installmentIds);
      setOfflinePaymentMap(pendingMap);
    } catch (error) {
      console.error('Error checking offline payments:', error);
    }
  }, [installments]);

  useEffect(() => {
    if (loanId) {
      fetchLoanSchedule(loanId);
    } else {
      fetchActiveLoans();
    }
  }, [loanId, filterDate]);

  useEffect(() => {
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOffline(false);
      // Small delay to ensure network is stable before fetching
      setTimeout(() => {
        const currentLoanId = loanIdRef.current;
        if (currentLoanId) {
          fetchLoanSchedule(currentLoanId);
        } else {
          fetchActiveLoans();
        }
      }, 1000);
    };
    const handleOffline = () => {
      setIsOffline(true);
      const currentLoanId = loanIdRef.current;
      if (!currentLoanId) {
        loadCachedActiveLoans();
      }
    };

    // Listen for payment sync completion
    const handlePaymentSync = async () => {
      // Refresh offline payment indicators after sync
      setTimeout(async () => {
        try {
          const currentInstallments = installmentsRef.current;
          if (currentInstallments.length > 0) {
            const installmentIds = currentInstallments.map(inst => inst.id);
            const pendingMap = await indexedDB.getPendingPaymentIdsByInstallmentIds(installmentIds);
            setOfflinePaymentMap(pendingMap);
          }
        } catch (error) {
          console.error('Error checking offline payments:', error);
        }
        
        if (navigator.onLine) {
          const currentLoanId = loanIdRef.current;
          if (currentLoanId) {
            fetchLoanSchedule(currentLoanId);
          } else {
            const currentSelectedLoan = selectedLoanRef.current;
            if (currentSelectedLoan) {
              fetchLoanSchedule(currentSelectedLoan.loan.id);
            }
          }
        }
      }, 1000);
    };

    // Listen for offline payment updates
    const handleOfflinePaymentUpdate = async () => {
      try {
        const currentInstallments = installmentsRef.current;
        if (currentInstallments.length > 0) {
          const installmentIds = currentInstallments.map(inst => inst.id);
          const pendingMap = await indexedDB.getPendingPaymentIdsByInstallmentIds(installmentIds);
          setOfflinePaymentMap(pendingMap);
        }
      } catch (error) {
        console.error('Error checking offline payments:', error);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('offlinePaymentSynced', handlePaymentSync);
    window.addEventListener('offlinePaymentUpdated', handleOfflinePaymentUpdate);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offlinePaymentSynced', handlePaymentSync);
      window.removeEventListener('offlinePaymentUpdated', handleOfflinePaymentUpdate);
    };
  }, []); // Empty dependency array - event listeners set up once, use refs for current values

  // Check offline payments whenever installments change
  useEffect(() => {
    if (installments.length > 0) {
      checkOfflinePayments();
    }
  }, [installments, checkOfflinePayments]);

  const loadCachedActiveLoans = async () => {
    try {
      const cached = await indexedDB.getCachedActiveLoans();
      if (cached && cached.length > 0) {
        setLoans(cached);
        setUsingCachedData(true);
        setSyncMessage({
          type: 'info',
          text: `Showing ${cached.length} cached loans. Data will refresh when connection is restored.`
        });
      } else {
        setSyncMessage({
          type: 'warning',
          text: 'No cached data available. Please connect to the internet to load loans.'
        });
      }
    } catch (error) {
      console.error('Error loading cached loans:', error);
      setSyncMessage({
        type: 'error',
        text: 'Failed to load cached data. Please connect to the internet.'
      });
    }
  };

  const fetchActiveLoans = async () => {
    // If offline, try to load cached data
    if (!navigator.onLine) {
      await loadCachedActiveLoans();
      setLoading(false);
      return;
    }

    setLoading(true);
    setSyncMessage(null); // Clear any previous error messages
    
    try {
      const response = await api.get('/collector/loans/active', {
        params: { collectorId: user.userId }
      });
      const data = response.data || [];
      
      // Successfully fetched fresh data - use it
      setLoans(data);
      setUsingCachedData(false);
      setSyncMessage(null); // Clear any previous messages
      
      // Cache the data for offline use
      if (data.length > 0) {
        await indexedDB.cacheActiveLoans(data);
      } else {
        // If no loans returned, still clear any cached data indicators
        setUsingCachedData(false);
      }
    } catch (error) {
      console.error('Error fetching active loans:', error);
      
      // Check if it's a network-related error (not a server error)
      const isNetworkError = !navigator.onLine || 
                            error.code === 'ERR_NETWORK' || 
                            error.code === 'ERR_INTERNET_DISCONNECTED' ||
                            error.message?.includes('Network') ||
                            error.message?.includes('network') ||
                            error.message?.includes('Failed to fetch') ||
                            error.message?.includes('fetch');
      
      // Only show cached data for network errors, not server errors
      if (isNetworkError) {
        // Network error - try to load cached data
        try {
          const cached = await indexedDB.getCachedActiveLoans();
          if (cached && cached.length > 0) {
            setLoans(cached);
            setUsingCachedData(true);
            setSyncMessage({
              type: 'warning',
              text: `Network error. Showing ${cached.length} cached loans. Please check your connection.`
            });
          } else {
            // No cached data available
            setSyncMessage({
              type: 'error',
              text: 'Network error. Please check your connection and try again.'
            });
            setLoans([]);
            setUsingCachedData(false);
          }
        } catch (cacheError) {
          console.error('Error loading cached loans:', cacheError);
          setSyncMessage({
            type: 'error',
            text: 'Network error. Please check your connection and try again.'
          });
          setLoans([]);
          setUsingCachedData(false);
        }
      } else {
        // Server error (401, 403, 500, etc.) - don't show cached data, show error
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        setSyncMessage({
          type: 'error',
          text: `Failed to load loans: ${errorMessage}`
        });
      setLoans([]);
        setUsingCachedData(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLoanSchedule = async (id) => {
    // If offline, try to load cached data
    if (!navigator.onLine) {
      try {
        const cached = await indexedDB.getCachedLoanSchedule(id);
        if (cached) {
          setSelectedLoan(cached);
          setInstallments(cached.installments || []);
          setUsingCachedData(true);
          setSyncMessage({
            type: 'info',
            text: 'Showing cached loan schedule. Data will refresh when connection is restored.'
          });
        } else {
          setSyncMessage({
            type: 'warning',
            text: 'No cached data available for this loan. Please connect to the internet.'
          });
        }
      } catch (error) {
        console.error('Error loading cached loan schedule:', error);
        setSyncMessage({
          type: 'error',
          text: 'Failed to load cached data. Please connect to the internet.'
        });
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setSyncMessage(null); // Clear any previous error messages
    
    try {
      const response = await api.get(`/collector/loans/${id}/schedule`);
      const data = response.data;
      setSelectedLoan(data);
      const allInstallments = data.installments || [];
      setInstallments(allInstallments);

      try {
        const today = new Date().toISOString().split('T')[0];
        const breakdownRes = await api.get(`/collector/loans/${id}/total-payable-breakdown`, {
          params: { date: today },
        });
        setPostMaturityBreakdown(breakdownRes.data?.postMaturityInterestBreakdown || null);
      } catch (breakdownErr) {
        console.warn('Could not load post-maturity interest breakdown', breakdownErr);
        setPostMaturityBreakdown(null);
      }
      
      // If date filter is provided, filter installments to show only due today + overdue
      if (filterDate) {
        const targetDate = new Date(filterDate);
        targetDate.setHours(0, 0, 0, 0);
        const filtered = allInstallments.filter(inst => {
          if (inst.installmentNumber === 1) return false; // Exclude first installment
          const dueDate = new Date(inst.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          // Show installments due on target date or overdue (due date < target date)
          return (dueDate.getTime() === targetDate.getTime() || dueDate.getTime() < targetDate.getTime()) &&
                 (inst.status === 'PENDING' || inst.status === 'PARTIAL' || inst.status === 'OVERDUE' || inst.status === 'LATE' || inst.status === 'PAID');
        });
        setFilteredInstallments(filtered);
        setShowFullSchedule(false);
      } else {
        setFilteredInstallments(allInstallments);
        setShowFullSchedule(true);
      }
      
      setUsingCachedData(false);
      
      // Clear any cached data message
      if (usingCachedData) {
        setSyncMessage(null);
      }
      
      // Cache the data for offline use
      if (data) {
        await indexedDB.cacheLoanSchedule(id, data);
      }
    } catch (error) {
      console.error('Error fetching loan schedule:', error);
      
      // Check if it's a network-related error
      const isNetworkError = !navigator.onLine || 
                            error.code === 'ERR_NETWORK' || 
                            error.code === 'ERR_INTERNET_DISCONNECTED' ||
                            error.message?.includes('Network') ||
                            error.message?.includes('network') ||
                            error.message?.includes('Failed to fetch') ||
                            error.message?.includes('fetch');
      
      // Always try to load cached data if available
      try {
        const cached = await indexedDB.getCachedLoanSchedule(id);
        if (cached) {
          setSelectedLoan(cached);
          const allInstallments = cached.installments || [];
          setInstallments(allInstallments);
          
          // Apply date filter if provided
          if (filterDate) {
            const targetDate = new Date(filterDate);
            targetDate.setHours(0, 0, 0, 0);
            const filtered = allInstallments.filter(inst => {
              if (inst.installmentNumber === 1) return false;
              const dueDate = new Date(inst.dueDate);
              dueDate.setHours(0, 0, 0, 0);
              return (dueDate.getTime() === targetDate.getTime() || dueDate.getTime() < targetDate.getTime()) &&
                     (inst.status === 'PENDING' || inst.status === 'PARTIAL' || inst.status === 'OVERDUE' || inst.status === 'LATE' || inst.status === 'PAID');
            });
            setFilteredInstallments(filtered);
            setShowFullSchedule(false);
          } else {
            setFilteredInstallments(allInstallments);
            setShowFullSchedule(true);
          }
          
          setUsingCachedData(true);
          setSyncMessage({
            type: 'warning',
            text: isNetworkError
              ? 'Network error. Showing cached loan schedule. Please check your connection.'
              : 'Unable to refresh data. Showing cached loan schedule.'
          });
        } else {
          // No cached data available
          const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
          if (isNetworkError) {
            setSyncMessage({
              type: 'error',
              text: 'Network error. Please check your connection and try again.'
            });
          } else {
            setSyncMessage({
              type: 'error',
              text: `Error fetching loan schedule: ${errorMessage}`
            });
          }
        }
      } catch (cacheError) {
        console.error('Error loading cached loan schedule:', cacheError);
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        if (isNetworkError) {
          setSyncMessage({
            type: 'error',
            text: 'Network error. Please check your connection and try again.'
          });
        } else {
          setSyncMessage({
            type: 'error',
            text: `Error fetching loan schedule: ${errorMessage}`
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewSchedule = (loan) => {
    navigate(`/collector/loan-schedules/${loan.loan.id}`);
  };

  const handleUpdateStatus = async () => {
    try {
      if (editMode) {
        // Editing an existing paid installment - must be online
        if (!navigator.onLine) {
          setSyncMessage({ 
            type: 'error', 
            text: 'Cannot edit a paid installment while offline. Please connect to the internet.' 
          });
          return;
        }

        if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
          setSyncMessage({ 
            type: 'error', 
            text: 'Please enter a valid payment amount.' 
          });
          return;
        }

        // Check if there's an approved request
        const approvedRequest = hasApprovedRequest(selectedInstallment);
        const requestId = approvedRequest ? paymentEditRequests[selectedInstallment.id]?.id : null;
        
        // Call edit payment endpoint
        const response = await api.post(`/collector/installments/${selectedInstallment.id}/payment/edit`, null, {
          params: {
            paymentAmount: parseFloat(paymentAmount),
            collectorId: user.userId,
            notes: notes || '',
            requestId: requestId || undefined,
          },
        });

        console.log('Edit payment response:', response.data);

        setSyncMessage({ 
          type: 'success', 
          text: 'Payment updated successfully! SMS sent to customer with updated payment details.' 
        });

        // Refresh installments
        if (loanId) {
          await fetchLoanSchedule(loanId);
        } else if (selectedLoan) {
          await fetchLoanSchedule(selectedLoan.loan.id);
        }

        setOpenDialog(false);
        setSelectedInstallment(null);
        setStatus('PAID');
        setNotes('');
        setPaymentAmount('');
        setEditMode(false);
        setTotalPayableBreakdown(null);
        return;
      }

      // New payment recording
      if (paymentAmount && parseFloat(paymentAmount) > 0) {
        // Use offline payment service which handles both online and offline cases
        const result = await offlinePaymentService.recordPayment(selectedInstallment.id, {
          paymentAmount: paymentAmount,
          collectorId: user.userId,
          notes: notes,
        });

        if (result.synced) {
          setSyncMessage({ 
            type: 'success', 
            text: 'Payment recorded successfully! SMS sent to customer with payment details.' 
          });
        } else {
          setSyncMessage({ 
            type: 'warning', 
            text: result.message || 'Payment saved offline. Will sync when connection is restored. SMS will be sent after sync.' 
          });
        }
        
        // Notify status indicator to refresh
        triggerStatusRefresh();
        
        // Update offline payment indicators
        if (!result.synced) {
          // Payment was saved offline, update the map
          setOfflinePaymentMap(prev => ({
            ...prev,
            [selectedInstallment.id]: true
          }));
        } else {
          // Payment was synced, remove from map
          setOfflinePaymentMap(prev => {
            const newMap = { ...prev };
            delete newMap[selectedInstallment.id];
            return newMap;
          });
        }
      } else {
        // Use old status update endpoint (only works online)
        if (!navigator.onLine) {
          setSyncMessage({ 
            type: 'error', 
            text: 'Cannot update status offline. Please enter a payment amount.' 
          });
          return;
        }

        await api.put(`/collector/installments/${selectedInstallment.id}/status`, null, {
          params: {
            status: status,
            collectorId: user.userId,
            notes: notes,
          },
        });
        setSyncMessage({ type: 'success', text: 'Status updated successfully!' });
      }
      
      // Refresh the schedule if online
      if (navigator.onLine) {
        if (loanId) {
          fetchLoanSchedule(loanId);
        } else if (selectedLoan) {
          fetchLoanSchedule(selectedLoan.loan.id);
        }
      }
      
      setOpenDialog(false);
      setSelectedInstallment(null);
      setStatus('PAID');
      setNotes('');
      setPaymentAmount('');
      setTotalPayableBreakdown(null);
      setEditMode(false);
    } catch (error) {
      console.error('Error updating status:', error);
      setSyncMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error updating installment status' 
      });
    }
  };

  // Check if payment is editable (within 24 hours or has approved request)
  const isPaymentEditable = (installment) => {
    if (installment.status !== 'PAID' || !installment.updatedAt) {
      return true; // Not paid yet, can be edited
    }
    const paymentTime = new Date(installment.updatedAt);
    const now = new Date();
    const hoursSincePayment = (now - paymentTime) / (1000 * 60 * 60);
    
    // Check if there's an approved request for this installment
    const request = paymentEditRequests[installment.id];
    if (request && request.status === 'APPROVED') {
      return true; // Has approved request, can be edited
    }
    
    return hoursSincePayment <= 24;
  };
  
  // Check if payment has a pending request
  const hasPendingRequest = (installment) => {
    const request = paymentEditRequests[installment.id];
    return request && request.status === 'PENDING';
  };
  
  // Check if payment has an approved request
  const hasApprovedRequest = (installment) => {
    const request = paymentEditRequests[installment.id];
    return request && request.status === 'APPROVED';
  };
  
  // Fetch payment edit requests for user
  const fetchPaymentEditRequests = useCallback(async () => {
    if (!user?.userId) return;
    try {
      const response = await api.get('/collector/payment-edit-requests', {
        params: { userId: user.userId }
      });
      const requests = response.data || [];
      const requestsMap = {};
      requests.forEach(req => {
        if (req.installment && req.installment.id) {
          requestsMap[req.installment.id] = req;
        }
      });
      setPaymentEditRequests(requestsMap);
    } catch (error) {
      console.error('Error fetching payment edit requests:', error);
    }
  }, [user?.userId]);
  
  // Submit payment edit request
  const submitPaymentEditRequest = async () => {
    if (!selectedInstallment || !requestedAmount || parseFloat(requestedAmount) <= 0) {
      setSyncMessage({
        type: 'error',
        text: 'Please enter a valid payment amount'
      });
      return;
    }
    
    try {
      const response = await api.post('/collector/payment-edit-requests', null, {
        params: {
          installmentId: selectedInstallment.id,
          requestedBy: user.userId,
          requestedPaymentAmount: requestedAmount,
          reason: requestReason || ''
        }
      });
      
      setSyncMessage({
        type: 'success',
        text: 'Payment edit request submitted successfully. Waiting for admin approval.'
      });
      
      setRequestDialogOpen(false);
      setRequestReason('');
      setRequestedAmount('');
      
      // Refresh requests
      await fetchPaymentEditRequests();
      
      // Refresh loan schedule
      if (selectedLoan) {
        fetchLoanSchedule(selectedLoan.id);
      }
    } catch (error) {
      setSyncMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to submit edit request'
      });
    }
  };
  
  // Fetch payment edit requests on mount
  useEffect(() => {
    fetchPaymentEditRequests();
  }, [fetchPaymentEditRequests]);

  const fetchTotalPayableBreakdown = async (loanId, targetDate) => {
    try {
      const response = await api.get(`/collector/loans/${loanId}/total-payable-breakdown`, {
        params: { date: targetDate }
      });
      setTotalPayableBreakdown(response.data);
    } catch (error) {
      console.error('Error fetching total payable breakdown:', error);
      setTotalPayableBreakdown(null);
    }
  };

  const openUpdateDialog = async (installment, mode = 'new') => {
    setSelectedInstallment(installment);
    setStatus(installment.status || 'PAID');
    
    // Fetch total payable breakdown for this installment's due date
    if (installment.loan?.id && installment.dueDate) {
      await fetchTotalPayableBreakdown(installment.loan.id, installment.dueDate);
    } else {
      setTotalPayableBreakdown(null);
    }
    
    // Check if this is edit mode (installment is already paid)
    const isPaid = installment.status === 'PAID' || 
                   (parseFloat(installment.paidAmount || 0) > 0 && 
                    parseFloat(installment.paidAmount || 0) >= parseFloat(installment.totalAmount || 0));
    
    if (mode === 'edit' || isPaid) {
      setEditMode(true);
      
      // Check if payment is editable (within 24 hours or has approved request)
      const editable = isPaymentEditable(installment);
      const approvedRequest = hasApprovedRequest(installment);
      
      if (!editable) {
        // Check if there's a pending request
        if (hasPendingRequest(installment)) {
          setSyncMessage({
            type: 'info',
            text: 'A payment edit request is pending admin approval. Please wait for approval before editing.'
          });
        } else {
          setSyncMessage({
            type: 'warning',
            text: 'This payment is older than 24 hours. Please submit an edit request for admin approval.'
          });
        }
        setOpenDialog(false);
        return;
      }
      
      // Pre-fill with requested amount if approved, otherwise current paid amount
      if (approvedRequest) {
        const request = paymentEditRequests[installment.id];
        setPaymentAmount(request.requestedPaymentAmount?.toFixed(2) || installment.paidAmount?.toFixed(2) || installment.totalAmount?.toFixed(2) || '');
      } else {
        setPaymentAmount(installment.paidAmount?.toFixed(2) || installment.totalAmount?.toFixed(2) || '');
      }
      setNotes(installment.notes || '');
    } else {
      setEditMode(false);
      // Pre-fill with total payable if available, otherwise use total due
      if (totalPayableBreakdown && totalPayableBreakdown.totalPayable) {
        setPaymentAmount(totalPayableBreakdown.totalPayable.toFixed(2));
      } else {
        setPaymentAmount(installment.totalAmount?.toFixed(2) || '');
      }
      setNotes('');
    }
    
    setOpenDialog(true);
  };

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'default';
      case 'OVERDUE':
        return 'error';
      case 'LATE':
        return 'warning';
      case 'PARTIAL':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Loading loan schedules...</Typography>
        </Box>
      </Box>
    );
  }

  // If viewing a specific loan schedule
  if (loanId && selectedLoan) {
    const loan = selectedLoan.loan;
    const customer = selectedLoan.customer;

    // Calculate summary statistics
    // Note: First installment (installmentNumber === 1) is already deducted, so exclude it from all counts
    const totalInstallments = installments.length;
    // Count only paid installments from installment 2 onwards (excluding first installment)
    const paidInstallments = installments.filter(i => 
      i.installmentNumber > 1 && 
      i.status === 'PAID'
    ).length;
    // Count only pending/partial installments from installment 2 onwards (excluding first installment)
    const pendingInstallments = installments.filter(i => 
      i.installmentNumber > 1 && 
      (i.status === 'PENDING' || i.status === 'PARTIAL')
    ).length;
    // Count only overdue/late installments from installment 2 onwards (excluding first installment)
    const overdueInstallments = installments.filter(i => 
      i.installmentNumber > 1 && 
      (i.status === 'OVERDUE' || i.status === 'LATE')
    ).length;
    const totalCollected = installments.reduce((sum, i) => sum + (parseFloat(i.paidAmount) || 0), 0);
    const totalDue = installments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
    const postMaturityInterest = parseFloat(loan.postMaturityInterestBalance || 0);
    const remainingBalance = Math.max(0, totalDue - totalCollected) + postMaturityInterest;
    
    // Calculate remaining installments (excluding first installment which is already deducted)
    // Count unpaid installments from installment 2 onwards
    const remainingInstallmentsCount = installments.filter(i => 
      i.installmentNumber > 1 && 
      i.status !== 'PAID'
    ).length;
    
    // Use filtered installments if date filter is active, otherwise use all installments
    const displayInstallments = filterDate && !showFullSchedule ? filteredInstallments : installments;

    return (
      <Box sx={{ pb: { xs: 2, sm: 3 } }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/collector/loan-schedules')} 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          Back to All Loans
        </Button>
        
        {/* Professional Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: '#0D47A1',
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
            }}
          >
            Loan Schedule
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600,
              color: '#1976D2',
              mb: 2,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            Loan #{loan.id}
          </Typography>
        </Box>

        <PostMaturityInterestPanel
          breakdown={postMaturityBreakdown}
          title="Late Payment Interest — Explain to Customer"
        />

        {/* Summary Statistics Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(13, 71, 161, 0.3)',
              borderRadius: 3,
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {totalInstallments}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Total Installments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(46, 125, 50, 0.3)',
              borderRadius: 3,
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {paidInstallments}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Paid
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(245, 124, 0, 0.3)',
              borderRadius: 3,
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {pendingInstallments}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #EF5350 0%, #D32F2F 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(211, 47, 47, 0.3)',
              borderRadius: 3,
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {overdueInstallments}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Overdue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(21, 101, 192, 0.3)',
              borderRadius: 3,
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Rs {remainingBalance.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Remaining Balance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Customer and Loan Details Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
              border: '1px solid rgba(25, 118, 210, 0.1)',
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ color: '#1976D2', mr: 1.5, fontSize: { xs: 24, sm: 28 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1', fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                    Customer Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Full Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      {customer.fullName || customer.name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      NIC Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      {customer.nic || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          Phone Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {customer.phoneNumber || customer.phone || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  {customer.address && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer.address}
                      </Typography>
                    </Grid>
                  )}
                  {customer.businessAddress && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Business Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer.businessAddress}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
              border: '1px solid rgba(25, 118, 210, 0.1)',
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalanceIcon sx={{ color: '#1976D2', mr: 1.5, fontSize: { xs: 24, sm: 28 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1', fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                    Loan Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Loan ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      #{loan.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Status
                    </Typography>
                    <Chip 
                      label={loan.status} 
                      color="success" 
                      size="small" 
                      sx={{ mt: 0.5, fontWeight: 600 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Approved Amount
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Rs {loan.approvedAmount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Document Charges
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Rs {loan.documentCharges?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(13, 71, 161, 0.1) 100%)',
                      borderRadius: 2,
                      border: '2px solid #1976D2',
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}>
                        Bank Deposit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0D47A1' }}>
                        Rs {loan.bankDeposit?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                  </Grid>
                  {loan.disbursementDate && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            Disbursement Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {new Date(loan.disbursementDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {loan.loanTemplate && (
          <Card sx={{ 
            mb: 3,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
            border: '1px solid rgba(25, 118, 210, 0.1)',
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1', mb: 2, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                Template Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Template Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {loan.loanTemplate.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Total Installments
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {loan.loanTemplate.numberOfInstallments}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    First Installment (Deducted)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    Rs {loan.loanTemplate.firstInstallment?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Remaining Installments
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {remainingInstallmentsCount} × Rs {loan.loanTemplate.installmentAmount?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Installment Schedule Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: '#0D47A1',
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              {filterDate && !showFullSchedule ? 'Due & Overdue Installments' : 'Installment Schedule'}
            </Typography>
            {filterDate && !showFullSchedule && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowFullSchedule(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                View Full Schedule
              </Button>
            )}
            {filterDate && showFullSchedule && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setShowFullSchedule(false)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Show Only Due & Overdue
              </Button>
            )}
          </Box>
          {filterDate && !showFullSchedule && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              Showing installments due on {new Date(filterDate).toLocaleDateString()} and overdue installments. Click "View Full Schedule" to see all installments.
            </Alert>
          )}
          {(!filterDate || showFullSchedule) && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              Click the edit icon to record payments for each installment. The first installment is already deducted from the loan amount.
            </Alert>
          )}
        </Box>

        <TableContainer 
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: isMobile ? 1200 : 'auto' }}>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
              }}>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>#</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Due Date</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Base Amount</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Late Interest</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Carryover</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Advance</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Total Due</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Paid</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Balance</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Paid Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Collector</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayInstallments
                .filter(inst => {
                  // If showing filtered view, exclude first installment
                  if (filterDate && !showFullSchedule) {
                    return inst.installmentNumber > 1;
                  }
                  return true; // Show all installments in full schedule
                })
                .sort((a, b) => a.installmentNumber - b.installmentNumber)
                .map((installment) => {
                  const totalDue = parseFloat(installment.totalAmount || 0);
                  const paid = parseFloat(installment.paidAmount || 0);
                  const balance = totalDue - paid;
                  const paymentStatus = balance === 0 ? 'Exact' : balance > 0 ? 'Underpaid' : 'Overpaid';
                  
                  return (
                    <TableRow 
                      key={installment.id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(25, 118, 210, 0.04)',
                        },
                        '&:nth-of-type(even)': {
                          bgcolor: 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {installment.installmentNumber}
                          </Typography>
                          {installment.installmentNumber === 1 && (
                            <Chip 
                              label="Deducted" 
                              size="small" 
                              color="warning" 
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {new Date(installment.dueDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Rs {installment.amount?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(installment.lateInterest || 0) > 0 ? (
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Late Interest Applied
                                </Typography>
                                <Typography variant="caption" component="div">
                                  Post-maturity interest applied after the loan end date on the total outstanding balance ({interestConfig?.overdueInterestRate?.toFixed(2) || '5'}% every {interestConfig?.interestCycleDays || 10} days).
                                </Typography>
                              </Box>
                            }
                            arrow
                          >
                            <Typography 
                              variant="body2" 
                              color="error.main" 
                              sx={{ 
                                fontWeight: 600, 
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                cursor: 'help',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                              }}
                            >
                              Rs {installment.lateInterest?.toFixed(2)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Rs 0.00
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {installment.carryoverAmount > 0 ? (
                          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Rs {installment.carryoverAmount?.toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {installment.advanceAmount > 0 ? (
                          <Typography variant="body2" color="info.main" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Rs {installment.advanceAmount?.toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0D47A1', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Rs {totalDue.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Rs {paid.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          sx={{
                            color: balance === 0 ? 'success.main' : balance > 0 ? 'error.main' : 'info.main',
                            fontWeight: 700,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          }}
                        >
                          {balance === 0 ? 'Rs 0.00' : balance > 0 ? `Rs ${balance.toFixed(2)}` : `Rs ${Math.abs(balance).toFixed(2)}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={installment.status || paymentStatus} 
                            size="small" 
                            color={balance === 0 ? 'success' : balance > 0 ? 'error' : 'info'}
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, height: 24 }}
                          />
                          {offlinePaymentMap[installment.id] && (
                            <Tooltip title="Payment recorded offline. Will sync when connection is restored.">
                              <Chip
                                icon={<CloudOffIcon sx={{ fontSize: 14 }} />}
                                label="Offline"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 22 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {installment.paidDate ? (
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {new Date(installment.paidDate).toLocaleDateString()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {installment.collector ? (
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {installment.collector.fullName}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {installment.installmentNumber === 1 ? (
                          <Chip 
                            label="Deducted" 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 24 }}
                          />
                        ) : (() => {
                          // Check if installment is already paid
                          const isPaid = installment.status === 'PAID' || 
                                        (parseFloat(installment.paidAmount || 0) > 0 && 
                                         parseFloat(installment.paidAmount || 0) >= parseFloat(installment.totalAmount || 0));
                          
                          if (isPaid) {
                            // Check if payment is editable (within 24 hours or has approved request)
                            const editable = isPaymentEditable(installment);
                            const pendingRequest = hasPendingRequest(installment);
                            const approvedRequest = hasApprovedRequest(installment);
                            
                            // Show Edit Payment button for paid installments
                            return (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {editable && (
                                  <Tooltip title={approvedRequest ? "Edit Payment (Approved)" : "Edit Payment"}>
                                    <span>
                                      <Button
                                        variant="contained"
                                        color={approvedRequest ? "success" : "secondary"}
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => openUpdateDialog(installment, 'edit')}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                          px: { xs: 1, sm: 1.5 },
                                          py: 0.5,
                                          minWidth: 'auto',
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                                {!editable && !pendingRequest && (
                                  <Tooltip title="Request to edit payment (requires admin approval)">
                                    <span>
                                      <Button
                                        variant="outlined"
                                        color="warning"
                                        size="small"
                                        startIcon={<PendingIcon />}
                                        onClick={() => {
                                          setSelectedInstallment(installment);
                                          setRequestedAmount(installment.paidAmount?.toFixed(2) || installment.totalAmount?.toFixed(2) || '');
                                          setRequestReason('');
                                          setRequestDialogOpen(true);
                                        }}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                          px: { xs: 1, sm: 1.5 },
                                          py: 0.5,
                                          minWidth: 'auto',
                                        }}
                                      >
                                        Request Edit
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                                {pendingRequest && (
                                  <Chip
                                    label="Request Pending"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                                  />
                                )}
                              </Box>
                            );
                          } else {
                            // Record Payment button - no date restrictions, collectors can mark any installment as paid
                            return (
                              <Tooltip title="Record Payment">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openUpdateDialog(installment, 'new')}
                                  sx={{
                                    '&:hover': {
                                      bgcolor: 'rgba(25, 118, 210, 0.1)',
                                    },
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                              </Tooltip>
                            );
                          }
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Update Status Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => {
            setOpenDialog(false);
            setEditMode(false);
            setSelectedInstallment(null);
            setPaymentAmount('');
            setNotes('');
            setTotalPayableBreakdown(null);
          }}
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 3 },
            },
          }}
        >
          <DialogTitle sx={{ 
            background: editMode 
              ? 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)'
              : 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
          }}>
            {editMode ? 'Edit Payment' : 'Record Payment'}
          </DialogTitle>
          <DialogContent>
            {selectedInstallment && (() => {
              const baseAmount = parseFloat(selectedInstallment.amount || 0);
              const lateInterest = parseFloat(selectedInstallment.lateInterest || 0);
              const carryover = parseFloat(selectedInstallment.carryoverAmount || 0);
              const advance = parseFloat(selectedInstallment.advanceAmount || 0);
              const totalDue = parseFloat(selectedInstallment.totalAmount || 0);
              const alreadyPaid = parseFloat(selectedInstallment.paidAmount || 0);
              // For edit mode, calculate remaining based on new payment amount entered
              // For new payment, calculate remaining based on current paid amount
              const newPaymentAmount = parseFloat(paymentAmount || 0);
              const remaining = editMode 
                ? totalDue - newPaymentAmount  // In edit mode, use the new amount being entered
                : totalDue - alreadyPaid;       // In new payment mode, use existing paid amount
              
              return (
                <Box sx={{ 
                  mb: 2, 
                  p: { xs: 2, sm: 3 }, 
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(13, 71, 161, 0.05) 100%)',
                  borderRadius: 3,
                  border: '1px solid rgba(25, 118, 210, 0.1)',
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: '#0D47A1',
                      mb: 2,
                      fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    }}
                  >
                    Payment Breakdown
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Base Amount:</Typography>
                    <Typography variant="body1">Rs {baseAmount.toFixed(2)}</Typography>
                  </Box>
                  
                  {lateInterest > 0 && (
                    <Box sx={{ mb: 1, p: 1.5, bgcolor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                        + Late Interest Applied:
                      </Typography>
                      <Typography variant="h6" color="error.dark" sx={{ fontWeight: 700, mb: 1 }}>
                        Rs {lateInterest.toFixed(2)}
                      </Typography>
                      {interestConfig && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Interest Rate:</strong> {interestConfig.overdueInterestRate?.toFixed(2)}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Interest cycle:</strong> every {interestConfig.interestCycleDays || 10} days after loan end date
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>Calculation Method:</strong> {interestConfig.overdueInterestRate?.toFixed(2)}% on total outstanding (principal + accumulated interest)
                          </Typography>
                        </Box>
                      )}
                      <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                        <Typography variant="caption" component="div">
                          <strong>How Interest Was Calculated:</strong><br/>
                          • No interest is charged before the loan&apos;s scheduled end date<br/>
                          • First {interestConfig?.overdueInterestRate?.toFixed(2) || '5'}% applies on the day after the final installment due date<br/>
                          • Additional {interestConfig?.overdueInterestRate?.toFixed(2) || '5'}% every {interestConfig?.interestCycleDays || 10} days on the full outstanding balance<br/>
                          {interestConfig && interestConfig.overdueInterestRate > 0 && (
                            <> • Accumulated post-maturity interest is stored at loan level and paid before principal</>
                          )}
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                  
                  {carryover > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">+ Carryover from Previous:</Typography>
                      <Typography variant="body1" color="warning.main">Rs {carryover.toFixed(2)}</Typography>
                    </Box>
                  )}
                  
                  {advance > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">- Advance Payment:</Typography>
                      <Typography variant="body1" color="info.main">Rs {advance.toFixed(2)}</Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  {/* Total Payable Breakdown */}
                  {totalPayableBreakdown && totalPayableBreakdown.totalPayable > 0 && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 2, border: '2px solid', borderColor: 'success.main' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon />
                        Total Payable for {new Date(selectedInstallment.dueDate).toLocaleDateString()}
                      </Typography>
                      
                      {totalPayableBreakdown.overdueInstallments > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Overdue Base Amount:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatCurrency(totalPayableBreakdown.totalOverdueBase || 0)}
                          </Typography>
                        </Box>
                      )}
                      
                      {totalPayableBreakdown.totalLateInterest > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Late Interest ({totalPayableBreakdown.interestRate?.toFixed(2)}%):
                          </Typography>
                          <Typography variant="body1" color="error.main" sx={{ fontWeight: 600 }}>
                            {formatCurrency(totalPayableBreakdown.totalLateInterest || 0)}
                          </Typography>
                        </Box>
                      )}
                      
                      {totalPayableBreakdown.hasCurrentDayInstallment && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Current Day Installment:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatCurrency(totalPayableBreakdown.currentDayBase || 0)}
                          </Typography>
                        </Box>
                      )}
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.dark' }}>
                        Total Payable: {formatCurrency(totalPayableBreakdown.totalPayable || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        = {formatCurrency(totalPayableBreakdown.totalOverdueBase || 0)} (Overdue) + {formatCurrency(totalPayableBreakdown.totalLateInterest || 0)} (Interest) + {formatCurrency(totalPayableBreakdown.currentDayBase || 0)} (Current Day)
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Show Total Payable prominently if available */}
                  {totalPayableBreakdown && totalPayableBreakdown.totalPayable > 0 ? (
                    <Box sx={{ mb: 2, p: 2.5, bgcolor: 'success.main', borderRadius: 2, border: '2px solid', borderColor: 'success.dark' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon sx={{ fontSize: 28 }} />
                        Total Payable Amount
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        {formatCurrency(totalPayableBreakdown.totalPayable)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                        This is the total amount the customer needs to pay, including all overdue installments, late interest, and the current day installment.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 1, p: 1.5, bgcolor: 'primary.light', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                        <strong>This Installment Amount:</strong>
                      </Typography>
                      <Typography variant="h5" color="primary.dark" sx={{ fontWeight: 700 }}>
                        {formatCurrency(totalDue)}
                      </Typography>
                      {lateInterest > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Includes base amount + late interest + carryover - advance
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {editMode && alreadyPaid > 0 && (
                    <Box sx={{ mb: 1, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Current Paid Amount:
                      </Typography>
                      <Typography variant="h6" color="warning.dark" sx={{ fontWeight: 700 }}>
                        Rs {alreadyPaid.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        This will be updated to the new amount you enter below.
                      </Typography>
                    </Box>
                  )}
                  
                  {!editMode && alreadyPaid > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Already Paid:</Typography>
                      <Typography variant="body1">Rs {alreadyPaid.toFixed(2)}</Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{editMode ? 'New Remaining Balance (after edit):' : 'Remaining Balance:'}</strong>
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color={remaining === 0 ? 'success.main' : remaining > 0 ? 'error.main' : 'info.main'}
                    >
                      {remaining === 0 
                        ? 'Rs 0.00 (Fully Paid)' 
                        : remaining > 0 
                          ? `Rs ${remaining.toFixed(2)} (Underpaid)`
                          : `Rs ${Math.abs(remaining).toFixed(2)} (Overpaid)`}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
            
            {editMode && selectedInstallment && (
              <>
                {isPaymentEditable(selectedInstallment) ? (
                  <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" component="div">
                      <strong>Editing Payment:</strong> You are editing an already paid installment. 
                      After saving, a new SMS will be sent to the customer with the updated payment details.
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" component="div">
                      <strong>Admin Approval Required:</strong> This payment is older than 24 hours. 
                      Editing requires admin approval. Please contact an administrator to proceed.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
            
            <TextField
              fullWidth
              label={editMode ? "Updated Payment Amount" : "Payment Amount"}
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              margin="normal"
              required
              helperText={editMode 
                ? "Enter the corrected payment amount. This will overwrite the previous payment."
                : "Enter the actual amount paid by the customer"}
              inputProps={{ min: 0, step: 0.01 }}
            />
            
            <TextField
              fullWidth
              label={editMode ? "Edit Notes" : "Notes"}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              margin="normal"
              placeholder={editMode 
                ? "Optional notes about this payment edit"
                : "Optional notes about this payment"}
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" component="div">
                <strong>{editMode ? 'Edit Payment Rules:' : 'Payment Rules:'}</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>If payment <strong>equals</strong> remaining balance: Installment marked as PAID</li>
                  <li>If payment <strong>less than</strong> remaining balance: Balance carried forward to next installment</li>
                  <li>If payment <strong>more than</strong> remaining balance: Extra amount applied to future installments</li>
                </ul>
                {!editMode && (
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: 'info.dark' }}>
                    ✓ An SMS will be automatically sent to the customer with payment details after recording.
                  </Typography>
                )}
                {editMode && (
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: 'warning.dark' }}>
                    ✓ A new SMS will be sent to the customer with updated payment details after editing.
                  </Typography>
                )}
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 2 }}>
            <Button 
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                minWidth: { xs: 100, sm: 120 },
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateStatus} 
              variant="contained" 
              disabled={
                !paymentAmount || 
                parseFloat(paymentAmount) <= 0 ||
                (editMode && selectedInstallment && !isPaymentEditable(selectedInstallment))
              }
              sx={{
                background: editMode
                  ? 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)'
                  : 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                minWidth: { xs: 120, sm: 150 },
                '&:hover': {
                  background: editMode
                    ? 'linear-gradient(135deg, #8E24AA 0%, #6A1B9A 100%)'
                    : 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              {editMode ? 'Update Payment' : 'Record Payment'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Edit Request Dialog */}
        <Dialog
          open={requestDialogOpen}
          onClose={() => {
            setRequestDialogOpen(false);
            setRequestReason('');
            setRequestedAmount('');
            setSelectedInstallment(null);
          }}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                Request Payment Edit
              </Typography>
              <IconButton onClick={() => {
                setRequestDialogOpen(false);
                setRequestReason('');
                setRequestedAmount('');
                setSelectedInstallment(null);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedInstallment && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    This payment is older than 24 hours. Please submit a request for admin approval to edit it.
                  </Typography>
                </Alert>

                <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Payment Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(selectedInstallment.paidAmount || selectedInstallment.totalAmount || 0)}
                  </Typography>
                </Card>

                <TextField
                  fullWidth
                  label="Requested Payment Amount"
                  type="number"
                  value={requestedAmount}
                  onChange={(e) => setRequestedAmount(e.target.value)}
                  margin="normal"
                  required
                  helperText="Enter the corrected payment amount you want to request"
                  inputProps={{ min: 0, step: 0.01 }}
                />

                <TextField
                  fullWidth
                  label="Reason for Edit"
                  multiline
                  rows={4}
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  margin="normal"
                  placeholder="Please provide a reason for requesting this payment edit..."
                  helperText="This will help the admin understand why the edit is needed"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 2 }}>
            <Button
              onClick={() => {
                setRequestDialogOpen(false);
                setRequestReason('');
                setRequestedAmount('');
                setSelectedInstallment(null);
              }}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitPaymentEditRequest}
              variant="contained"
              disabled={!requestedAmount || parseFloat(requestedAmount) <= 0}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                },
              }}
            >
              Submit Request
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Filter loans by NIC and Loan Number
  const filteredLoans = loans.filter((loanData) => {
    const loan = loanData.loan;
    const customer = loanData.customer;
    
    // Filter by NIC
    if (nicFilter.trim()) {
      const nic = customer?.nic || '';
      if (!nic.toLowerCase().includes(nicFilter.toLowerCase().trim())) {
        return false;
      }
    }
    
    // Filter by Loan Number
    if (loanNumberFilter.trim()) {
      const loanId = loan?.id?.toString() || '';
      const searchValue = loanNumberFilter.trim();
      if (!loanId.includes(searchValue)) {
        return false;
      }
    }
    
    return true;
  });

  // List view of all active loans
  return (
    <Box>
      <Snackbar
        open={!!syncMessage}
        autoHideDuration={6000}
        onClose={() => setSyncMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSyncMessage(null)}
          severity={syncMessage?.type || 'info'}
          sx={{ width: '100%' }}
        >
          {syncMessage?.text}
        </Alert>
      </Snackbar>
      
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are currently offline. {usingCachedData 
            ? 'Showing cached data. Payments will be saved offline and synced when connection is restored.'
            : 'No cached data available. Please connect to the internet to load loans.'}
        </Alert>
      )}
      
      {usingCachedData && navigator.onLine && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing cached data. Refreshing from server...
        </Alert>
      )}
      
      {/* Professional Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: '#0D47A1',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
          }}
        >
          Active Loan Schedules
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          View and manage installment schedules for all active loans
        </Typography>
      </Box>

      {/* NIC and Loan Number Filter */}
      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <SearchIcon sx={{ color: '#1976D2' }} />
          <TextField
            label="Filter by NIC"
            variant="outlined"
            value={nicFilter}
            onChange={(e) => setNicFilter(e.target.value)}
            placeholder="Enter NIC to search"
            sx={{ 
              minWidth: { xs: '100%', sm: 250 },
              flexGrow: { xs: 1, sm: 0 },
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <TextField
            label="Filter by Loan Number"
            variant="outlined"
            value={loanNumberFilter}
            onChange={(e) => setLoanNumberFilter(e.target.value)}
            placeholder="Enter Loan Number"
            sx={{ 
              minWidth: { xs: '100%', sm: 250 },
              flexGrow: { xs: 1, sm: 0 },
            }}
            InputProps={{
              startAdornment: <AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
            }}
          />
          {(nicFilter || loanNumberFilter) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setNicFilter('');
                setLoanNumberFilter('');
              }}
              sx={{ textTransform: 'none' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {loans.length === 0 ? (
        <Alert severity="info">No active loans found.</Alert>
      ) : filteredLoans.length === 0 ? (
        <Alert severity="info">No loans found matching the NIC filter.</Alert>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
            overflowX: 'auto',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
              }}>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Loan ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Customer Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>NIC</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Phone</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Loan Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Disbursement Date</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLoans.map((loanData) => {
                const loan = loanData.loan;
                const customer = loanData.customer;
                return (
                  <TableRow 
                    key={loan.id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(25, 118, 210, 0.04)',
                      },
                      '&:nth-of-type(even)': {
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        #{loan.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {customer.fullName || customer.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {customer.nic || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {customer.phoneNumber || customer.phone || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0D47A1', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Rs {loan.approvedAmount?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={loan.status} 
                        color="success" 
                        size="small"
                        sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell>
                      {loan.disbursementDate ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {new Date(loan.disbursementDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ListIcon />}
                        onClick={() => handleViewSchedule(loanData)}
                        sx={{
                          background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                          },
                        }}
                      >
                        View Schedule
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default LoanSchedules;


